import { useRef, useState, useEffect } from 'react';
import { T, useMessages } from 'gt-react';
import { Camera } from '@phosphor-icons/react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChatMessage } from './ChatMessage';
import { ChatMarkdown } from './chat-markdown';
import { StreamingCaret } from './StreamingCaret';
import { ThinkingReasoning } from './ThinkingReasoning';
import { useCatchUpText } from './useCatchUpText';
import { useStickToBottom } from './useStickToBottom';
import { useChatWindowReporter } from '@/context/chatWindowSize';
import { layoutTurn, toolsLead } from './turnLayout';
import { cn } from '@/lib/utils';
import { getFriendlyToolName } from '@/lib/workflows/toolFriendlyNames';
import { NAMI_TOOL_LABELS, isNamiToolName } from './nami/namiToolLabels';
import type {
  Message,
  MessageToolResult,
  ToolCall,
} from '@/context/agent-state.context';
import type { ReactNode } from 'react';

interface ChatMessageListProps {
  messages: Message[];
  streamingText?: string;
  isLoading: boolean;
  isCapturingScreen?: boolean;
  error?: string | null;
  activeToolCalls?: ToolCall[];
  className?: string;
  messageMaxWidth?: string;
  /** Optional renderer for tool results attached to assistant messages */
  renderToolResult?: (
    message: Message,
    toolResult: MessageToolResult,
  ) => ReactNode;
  excludeToolResultIds?: ReadonlySet<string>;
  onFocusNodes?: (nodeIds: string[]) => void;
  /**
   * What the voice said on a call while the agent works ("let me check"),
   * shown above the tools like reply text written before them.
   */
  spokenLead?: string;
}

/**
 * Progress kind reported by every workflow draft mutation tool. Kept in
 * sync with `DraftProgressKind` in the backend tools file. The frontend
 * groups consecutive same-kind entries into a single collapsible bucket
 * so the chat log stays compact even on multi-step builds.
 */
type ProgressKind =
  | 'set_metadata'
  | 'add_step'
  | 'update_config'
  | 'remove_step'
  | 'add_edge'
  | 'remove_edge'
  | 'set_static_input'
  | 'clear_static_input'
  | 'reset';

interface Progress {
  kind: ProgressKind;
  label: string;
}

const KIND_INFO: Record<
  ProgressKind,
  { verb: string; noun: string; nounPlural: string }
> = {
  set_metadata: { verb: 'Updated', noun: 'metadata', nounPlural: 'metadata' },
  add_step: { verb: 'Added', noun: 'step', nounPlural: 'steps' },
  update_config: { verb: 'Configured', noun: 'step', nounPlural: 'steps' },
  remove_step: { verb: 'Removed', noun: 'step', nounPlural: 'steps' },
  add_edge: { verb: 'Connected', noun: 'edge', nounPlural: 'edges' },
  remove_edge: { verb: 'Disconnected', noun: 'edge', nounPlural: 'edges' },
  set_static_input: { verb: 'Pinned', noun: 'input', nounPlural: 'inputs' },
  clear_static_input: { verb: 'Unpinned', noun: 'input', nounPlural: 'inputs' },
  reset: { verb: 'Reset', noun: 'draft', nounPlural: 'drafts' },
};

function extractProgress(result: unknown): Progress | null {
  if (!result || typeof result !== 'object') return null;
  const progress = (result as { progress?: unknown }).progress;
  if (!progress || typeof progress !== 'object') return null;
  const { kind, label } = progress as { kind?: unknown; label?: unknown };
  if (typeof kind !== 'string' || typeof label !== 'string') return null;
  if (!(kind in KIND_INFO)) return null;
  return { kind: kind as ProgressKind, label };
}

function extractNodeIds(tc: ToolCall): string[] {
  const ids: string[] = [];
  const args = tc.args as Record<string, unknown>;

  switch (tc.toolName) {
    case 'draft_add_step': {
      if (tc.result && typeof tc.result === 'object') {
        const result = tc.result as Record<string, unknown>;
        const progress = result.progress as Record<string, unknown> | undefined;
        if (progress && typeof progress.stepId === 'string') {
          ids.push(progress.stepId);
        } else if (typeof result.stepId === 'string') {
          ids.push(result.stepId);
        }
      }
      break;
    }
    case 'draft_remove_step':
    case 'draft_update_step_config': {
      if (typeof args.stepId === 'string') {
        ids.push(args.stepId);
      }
      break;
    }
    case 'draft_add_edge':
    case 'draft_remove_edge': {
      if (typeof args.sourceStepId === 'string') {
        ids.push(args.sourceStepId);
      }
      if (typeof args.targetStepId === 'string') {
        ids.push(args.targetStepId);
      }
      break;
    }
  }

  return ids;
}

interface ProgressBucket {
  /** Bucket id is the first toolCallId — stable across renders */
  id: string;
  kind: ProgressKind;
  /** Labels of the completed entries in this bucket, in order */
  labels: string[];
  /** Pending entries in this bucket (most recent last) */
  pending: ToolCall[];
  nodeIds: string[];
}

function bucketizeToolCalls(toolCalls: readonly ToolCall[]): ProgressBucket[] {
  const buckets: ProgressBucket[] = [];
  // Same-kind ops nest into one bucket regardless of interleaving with
  // other kinds. The bucket is positioned where its FIRST entry landed
  // so the user still gets a sense of when each phase started.
  const bucketByKind = new Map<ProgressKind, ProgressBucket>();

  for (const tc of toolCalls) {
    if (tc.status === 'pending') continue;
    const progress = extractProgress(tc.result);
    if (!progress) continue;

    const nodeIds = extractNodeIds(tc);
    const existing = bucketByKind.get(progress.kind);
    if (existing) {
      existing.labels.push(progress.label);
      existing.nodeIds.push(...nodeIds);
    } else {
      const bucket: ProgressBucket = {
        id: tc.toolCallId,
        kind: progress.kind,
        labels: [progress.label],
        pending: [],
        nodeIds: [...nodeIds],
      };
      bucketByKind.set(progress.kind, bucket);
      buckets.push(bucket);
    }
  }

  return buckets;
}

function bucketSummary(bucket: ProgressBucket): string {
  const info = KIND_INFO[bucket.kind];
  if (bucket.kind === 'set_metadata') {
    // metadata is always 1 — collapse into a verbatim line
    return 'Updated workflow metadata';
  }
  if (bucket.labels.length === 1) {
    // Single-item buckets render the label inline so the user doesn't
    // have to expand to see what changed
    if (
      bucket.kind === 'add_edge' ||
      bucket.kind === 'remove_edge' ||
      bucket.kind === 'set_static_input' ||
      bucket.kind === 'clear_static_input'
    ) {
      return `${info.verb} ${bucket.labels[0]}`;
    }
    return `${info.verb} "${bucket.labels[0]}"`;
  }
  return `${info.verb} ${bucket.labels.length} ${info.nounPlural}`;
}

function ProgressLog({
  toolCalls,
  onFocusNodes,
}: {
  toolCalls: readonly ToolCall[];
  onFocusNodes?: (nodeIds: string[]) => void;
}) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const buckets = bucketizeToolCalls(toolCalls);

  if (buckets.length === 0) return null;

  const toggle = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="space-y-1">
      {buckets.map((bucket) => {
        const isExpanded = expanded[bucket.id] ?? false;
        const expandable = bucket.labels.length > 1;
        const isClickable =
          bucket.nodeIds.length > 0 && onFocusNodes !== undefined;
        return (
          <div key={bucket.id} className="text-sm text-stone-500">
            <button
              type="button"
              className={cn(
                'flex items-center gap-2 text-left w-full',
                expandable && 'hover:text-stone-700',
                isClickable &&
                  'cursor-pointer hover:text-foreground hover:underline',
              )}
              onClick={
                isClickable
                  ? () => onFocusNodes([...new Set(bucket.nodeIds)])
                  : expandable
                    ? () => toggle(bucket.id)
                    : undefined
              }
              disabled={!expandable && !isClickable}
            >
              <span className="inline-block w-1 h-1 bg-stone-400 rounded-full shrink-0" />
              <span>{bucketSummary(bucket)}</span>
              {expandable && (
                <span
                  role="button"
                  tabIndex={0}
                  className="text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(bucket.id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      e.stopPropagation();
                      toggle(bucket.id);
                    }
                  }}
                >
                  {/* eslint-disable-next-line i18next/no-literal-string -- UI glyph, not translatable */}
                  {isExpanded ? '▾' : '▸'}
                </span>
              )}
            </button>
            {expandable && isExpanded && (
              <ul className="ml-6 mt-0.5 space-y-0.5 text-xs text-stone-500">
                {bucket.labels.map((label, i) => (
                  <li key={i}>
                    {'·'} {label}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The row label while a tool call runs. The backend names most tools; the
 * assistant's harness action tools ship no `userFacingName`, so they read
 * from the translated label map before the generic humanizer.
 */
function toolSentence(tc: ToolCall, m: ReturnType<typeof useMessages>): string {
  if (tc.userFacingName) return tc.userFacingName;
  if (isNamiToolName(tc.toolName)) return m(NAMI_TOOL_LABELS[tc.toolName]);
  return getFriendlyToolName(tc.toolName);
}

function useThinkingElapsed(thinking: boolean): number {
  const thinkingStartedAt = useRef<number | undefined>(undefined);
  const [elapsedSeconds, setElapsedSeconds] = useState(1);

  useEffect(() => {
    if (!thinking) return undefined;
    thinkingStartedAt.current = Date.now();
    setElapsedSeconds(1);
    const id = setInterval(() => {
      const start = thinkingStartedAt.current;
      if (start === undefined) return;
      setElapsedSeconds(Math.max(1, Math.round((Date.now() - start) / 1000)));
    }, 1000);
    return () => clearInterval(id);
  }, [thinking]);

  return elapsedSeconds;
}

function ChatListShell({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <ScrollArea className={cn('flex-1 min-h-0 px-3', className)}>
      {children}
    </ScrollArea>
  );
}

function ChatTurn({
  msg,
  shown,
  messageMaxWidth,
  renderToolResult,
  excludeToolResultIds,
}: {
  msg: Message;
  /** The part of the reply drawn here, when the rest is above the tools. */
  shown?: string;
  messageMaxWidth: string;
  renderToolResult?: ChatMessageListProps['renderToolResult'];
  excludeToolResultIds?: ReadonlySet<string>;
}) {
  const toolResults =
    renderToolResult && msg.role === 'assistant'
      ? msg.toolResults?.filter(
          (tr) => !excludeToolResultIds?.has(tr.toolCallId),
        )
      : undefined;

  // An assistant turn can be tool results alone (e.g. a voice call, where
  // the reply text was spoken); it gets no empty text bubble.
  const showBubble = msg.role === 'user' || Boolean(msg.content);

  return (
    <div>
      {showBubble && (
        <ChatMessage
          role={msg.role}
          content={msg.content}
          shown={shown}
          maxWidth={messageMaxWidth}
          hasScreenshot={msg.hasScreenshot}
          screenshotDataUrl={msg.screenshotDataUrl}
          dbMessageId={msg.dbMessageId}
          traceId={msg.traceId}
          observationId={msg.observationId}
          attachments={msg.attachments}
          badge={msg.badge}
          voice={msg.channel === 'voice'}
        />
      )}
      {toolResults?.map((tr) => (
        <div key={tr.toolCallId} className="mt-2 ml-0">
          {renderToolResult?.(msg, tr)}
        </div>
      ))}
    </div>
  );
}

function CapturingScreenNotice() {
  return (
    <div className="flex justify-start">
      <div className="flex items-center gap-2 p-2.5 text-sm text-stone-500">
        <Camera className="h-4 w-4 animate-pulse" weight="fill" />
        <span>
          <T context="chat capturing screen indicator">Capturing screen...</T>
        </span>
      </div>
    </div>
  );
}

function groupConsecutiveSentences(sentences: readonly string[]): string[] {
  const grouped: string[] = [];
  for (const sentence of sentences) {
    if (grouped.at(-1) !== sentence) grouped.push(sentence);
  }
  return grouped;
}

const STREAMING_CARET = <StreamingCaret />;

function StreamingReply({
  text,
  maxWidth,
  caret = true,
}: {
  text: string;
  maxWidth: string;
  /** False for text that is done while the reply goes on elsewhere. */
  caret?: boolean;
}) {
  return (
    <div className="flex justify-start">
      <div className={cn(maxWidth, 'text-foreground')}>
        <ChatMarkdown trailing={caret ? STREAMING_CARET : undefined}>
          {text}
        </ChatMarkdown>
      </div>
    </div>
  );
}

/** What was said before the turn's tools, drawn above them; done, no caret. */
function TurnLead({
  texts,
  maxWidth,
}: {
  texts: (string | undefined)[];
  maxWidth: string;
}) {
  return texts
    .filter(Boolean)
    .map((text) => (
      <StreamingReply
        key={text}
        text={text ?? ''}
        maxWidth={maxWidth}
        caret={false}
      />
    ));
}

export function ChatErrorBanner({
  error,
  maxWidth,
}: {
  error: string;
  maxWidth: string;
}) {
  return (
    <div className="flex justify-start">
      <div
        role="alert"
        className={cn(
          maxWidth,
          'rounded-2xl bg-red-50 px-3.5 py-2.5 text-red-800',
        )}
      >
        <p className="text-sm font-medium">
          <T context="chat error message heading">Error</T>
        </p>
        <p className="text-sm leading-relaxed">{error}</p>
      </div>
    </div>
  );
}

export function ChatMessageList({
  messages,
  streamingText,
  isLoading,
  isCapturingScreen = false,
  error,
  activeToolCalls = [],
  className,
  messageMaxWidth = 'max-w-[85%]',
  renderToolResult,
  excludeToolResultIds,
  onFocusNodes,
  spokenLead,
}: ChatMessageListProps) {
  const m = useMessages();
  const shownStreamingText = useCatchUpText(streamingText ?? '');
  // Reply text before the tools is drawn above them: no reply yet after it.
  const replyText = (streamingText ?? '').slice(
    toolsLead(activeToolCalls).length,
  );
  const thinking =
    isLoading &&
    !isCapturingScreen &&
    !replyText &&
    messages.at(-1)?.role === 'user';
  const showReasoning = thinking || activeToolCalls.length > 0;
  const elapsedSeconds = useThinkingElapsed(thinking);
  const setContentNode = useStickToBottom();
  const reportWindowNode = useChatWindowReporter();

  // `error` counts as content: the error block lives in the main return, so
  // without this an error-only state would fall through to the empty-state
  // placeholder and the error would never render.
  const hasContent =
    messages.length > 0 ||
    isLoading ||
    Boolean(streamingText) ||
    Boolean(error);

  if (!hasContent) {
    return (
      <ChatListShell className={className}>
        {/* Measured here too: the first message of a new chat is sent from this state. */}
        <div
          ref={reportWindowNode}
          className="flex items-center justify-center h-full py-8"
        >
          <p className="text-sm text-muted-foreground">
            <T context="chat message list empty state prompt">
              Start a conversation with the AI assistant.
            </T>
          </p>
        </div>
      </ChatListShell>
    );
  }

  const turn = layoutTurn({
    messages,
    toolCalls: activeToolCalls,
    streamingText: shownStreamingText,
    streaming: isLoading,
  });
  const renderTurn = (msg: Message) => (
    <ChatTurn
      key={msg.id}
      msg={msg}
      shown={msg === turn.after[0] ? turn.replyShown : undefined}
      messageMaxWidth={messageMaxWidth}
      renderToolResult={renderToolResult}
      excludeToolResultIds={excludeToolResultIds}
    />
  );

  return (
    <ChatListShell className={className}>
      <div
        ref={(node) => {
          setContentNode(node);
          reportWindowNode(node);
        }}
        className="space-y-4 py-4"
      >
        {turn.before.map(renderTurn)}
        {isCapturingScreen && messages.at(-1)?.role === 'user' && (
          <CapturingScreenNotice />
        )}
        <TurnLead texts={[spokenLead, turn.lead]} maxWidth={messageMaxWidth} />
        {showReasoning && (
          <ThinkingReasoning
            sentences={groupConsecutiveSentences(
              activeToolCalls.map((tc) => toolSentence(tc, m)),
            )}
            thinking={thinking}
            elapsedSeconds={elapsedSeconds}
            liveToolName={activeToolCalls.at(-1)?.toolName}
          />
        )}
        <ProgressLog toolCalls={activeToolCalls} onFocusNodes={onFocusNodes} />
        {turn.after.map(renderTurn)}
        {turn.tail ? (
          <StreamingReply text={turn.tail} maxWidth={messageMaxWidth} />
        ) : null}
        {error ? (
          <ChatErrorBanner error={error} maxWidth={messageMaxWidth} />
        ) : null}
      </div>
    </ChatListShell>
  );
}
